import { useEffect, useRef, useState } from 'react';
import { listStoneQuoteMessages, postStoneQuoteMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Send, Loader2, MessageSquare } from 'lucide-react';

// Per-quote negotiation thread.
// Only admin + the supplier who owns the quote can see this (enforced server-side).
// Polls every 5s like the existing task ChatPanel.
export default function StoneQuoteChat({ quoteId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await listStoneQuoteMessages(quoteId);
      setMessages(res.data.data || []);
    } catch (e) {
      // ignore transient errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!quoteId) return;
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await postStoneQuoteMessage(quoteId, text);
      setMessages(prev => [...prev, res.data.data]);
      setBody('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[280px] bg-white border rounded-lg" data-testid="stone-quote-chat">
      <div className="px-3 py-2 border-b flex items-center gap-2 text-xs font-semibold text-slate-600">
        <MessageSquare size={14} />
        Negotiation
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[400px]">
        {loading ? (
          <p className="text-xs text-muted-foreground text-center py-6">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">No messages yet. Start the conversation.</p>
        ) : (
          messages.map(m => {
            const isMine = m.sender_id === currentUser?.id;
            return (
              <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                  isMine ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-800'
                }`}>
                  <div className="text-[10px] opacity-75 mb-0.5">
                    {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t p-2 flex gap-2">
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
          placeholder="Type a message… (Ctrl+Enter to send)"
          className="flex-1 text-xs p-2 border rounded resize-none min-h-[40px]"
          rows={1}
          data-testid="stone-chat-input"
        />
        <Button onClick={send} disabled={sending || !body.trim()} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="stone-chat-send">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </div>
    </div>
  );
}
